/**
 * ReviewSignOff Component
 *
 * Final sign-off section for completing a review.
 * Includes checklist validation and completion confirmation.
 */

import { useState, useCallback } from 'react';
import { CheckCircle, FileSignature, Clock } from 'lucide-react';
import { HumanReviewChecklist } from '../Limitations';

interface ReviewSignOffProps {
  reviewId: string;
  onComplete: () => Promise<void>;
  isCompleted: boolean;
  completedAt?: string;
  completedBy?: string;
}

function formatCompletionDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const dateStr = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  if (diffDays === 0) {
    return `Today at ${timeStr}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${timeStr}`;
  } else {
    return `${dateStr} at ${timeStr}`;
  }
}

export function ReviewSignOff({
  reviewId,
  onComplete,
  isCompleted,
  completedAt,
  completedBy,
}: ReviewSignOffProps) {
  const [isChecklistComplete, setIsChecklistComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChecklistComplete = useCallback((allChecked: boolean) => {
    setIsChecklistComplete(allChecked);
  }, []);

  const handleComplete = async () => {
    if (!isChecklistComplete) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete review');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Already completed - show success state
  if (isCompleted) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <FileSignature size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review Sign-Off</h3>
          </div>
        </div>

        {/* Success content */}
        <div className="px-6 py-6">
          <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle size={24} className="text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <h4 className="text-base font-semibold text-green-800 dark:text-green-300">Review Completed</h4>
              {completedBy && completedAt && (
                <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                  Reviewed by <span className="font-medium">{completedBy}</span> on{' '}
                  {formatCompletionDate(completedAt)}
                </p>
              )}
              {completedAt && !completedBy && (
                <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                  Completed {formatCompletionDate(completedAt)}
                </p>
              )}
            </div>
          </div>

          {/* Completion timestamp */}
          {completedAt && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock size={14} />
              <span>
                Signed off: {new Date(completedAt).toLocaleString('en-US', {
                  dateStyle: 'full',
                  timeStyle: 'short',
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not completed - show sign-off form
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
            <FileSignature size={20} className="text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review Sign-Off</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6">
        {/* Checklist */}
        <HumanReviewChecklist onComplete={handleChecklistComplete} disabled={isSubmitting} />

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Complete button */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleComplete}
            disabled={!isChecklistComplete || isSubmitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Completing Review...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Complete Review
              </>
            )}
          </button>

          {!isChecklistComplete && (
            <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
              Complete all checklist items above to enable sign-off
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
